// src/pages/RegistrationForm.tsx
// Multi-frontend CORS-safe registration form
//
// ─── HOW URL RESOLUTION WORKS ─────────────────────────────────────────────────
// Each frontend project controls its own .env:
//   Product frontend  (port 5173) → VITE_API_URL=http://localhost:3001
//   Public  frontend  (port 5174) → VITE_API_URL=          (empty → Vite proxy)
//
// When VITE_API_URL is empty, ALL fetch calls use RELATIVE paths ('/health',
// '/api/auth/register') which the Vite dev server proxies to localhost:3001 —
// no cross-origin request is made and CORS is completely bypassed.
//
// The `apiBase` prop is still IGNORED for URL construction — this file reads
// VITE_API_URL directly so its behaviour can't be changed from a parent.
// (Historically the prop was actively WRONG: App.tsx used `||` not `??`, so
// VITE_API_BASE='' fell through to a hardcoded "http://localhost:3001" that got
// baked into the production bundle. App.tsx now reads VITE_API_URL ?? '' too,
// so the prop agrees with this value — but reading the env here keeps the rule
// local and unbreakable.)
// ─────────────────────────────────────────────────────────────────────────────

// src/pages/RegistrationForm.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, EyeOff, User, Mail, Lock, CheckCircle, AlertCircle, Loader2, Shield, ArrowRight } from 'lucide-react';
import Turnstile from '../components/Turnstile';
import { log } from '../utils/logger';
import CheckYourInbox from './CheckYourInbox';
import { promptGoogleSignIn, googleUnavailableMessage } from '../utils/googleSignIn';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// ✅ CORS-safe: reads from THIS project's .env only.
//    Public-frontend  → VITE_API_URL=          → '' → relative path → Vite proxy → no CORS ✅
//    Product-frontend → VITE_API_URL=http://localhost:3001 → direct call (CORS_ORIGIN covers it) ✅
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

interface RegistrationFormProps {
  onRegisterSuccess: (token: string, user: any, refreshToken?: string) => void;
  onSwitchToLogin?: () => void;
  dark?: boolean;
  apiBase?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: { id: { initialize: (c: any) => void; renderButton: (p: HTMLElement, o: any) => void; prompt: () => void } };
    };
  }
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onRegisterSuccess, onSwitchToLogin, dark = true,
  // apiBase prop received but NOT used for URL construction — see header comment above.
  // URL is sourced from VITE_API_URL env var so each project's .env controls routing.
}) => {
  const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '' });
  // Set when /register succeeds. Registration no longer returns a session — the
  // account must confirm its address first — so the form is replaced by the
  // "check your inbox" panel rather than navigating into the app.
  const [awaitingVerification, setAwaitingVerification] = useState<
    { email: string; emailSent: boolean } | null
  >(null);
  // Turnstile is mounted unconditionally and a verified token is MANDATORY for
  // registration — the backend rejects /api/auth/register without one. It used
  // to appear only after the rate limiter demanded it; with email verification
  // not live yet (no SMTP) this challenge is the only bot gate on sign-up.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // Tokens are single-use: bump this after a failed submit to get a fresh one.
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [strength, setStrength] = useState(0);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  // Stable ref so the GSI callback always calls the latest version of handleGoogle,
  // even if the component re-renders between script-load and user interaction.
  const handleGoogleRef = useRef<((response: any) => void) | undefined>(undefined);

  useEffect(() => { checkHealth(); }, []);

  const checkHealth = async () => {
    setBackendStatus('checking');
    try {
      const r = await fetch(`${API_BASE_URL}/health`, { signal: AbortSignal.timeout(5000) });
      setBackendStatus(r.ok ? 'online' : 'offline');
      if (!r.ok) setError(`Server responded with status ${r.status}`);
    } catch (e) {
      setBackendStatus('offline');
      setError('Cannot connect to server. Please try again.');
      log.error('RegistrationForm', 'Health check failed', { err: String(e) });
    }
  };

  // Keep the ref in sync so the GSI callback always has the latest handler.
  const handleGoogle = useCallback(async (response: any) => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Google sign-up failed'); return; }
      localStorage.setItem('authToken', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      onRegisterSuccess(data.accessToken, data.user, data.refreshToken);
    } catch (_e) { setError('Google sign-up failed.'); }
    finally { setLoading(false); }
  }, [onRegisterSuccess]);

  useEffect(() => { handleGoogleRef.current = handleGoogle; }, [handleGoogle]);

  const handleGoogleClick = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google sign-in is not configured (VITE_GOOGLE_CLIENT_ID missing).');
      return;
    }
    if (!window.google) {
      setError('Google sign-in is still loading — please try again in a moment.');
      return;
    }
    setError(null);
    // prompt() is fire-and-forget: an unauthorized origin or a browser blocking
    // third-party cookies produces no dialog and no exception. Surface it.
    promptGoogleSignIn((reason) => setError(googleUnavailableMessage(reason)));
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      log.warn('RegistrationForm', '[Auth] VITE_GOOGLE_CLIENT_ID is not set — Google sign-in disabled.');
      return;
    }

    // Stable proxy so GSI always dispatches to the latest handler via the ref.
    const stableCallback = (response: any) => handleGoogleRef.current?.(response);

    const initGSI = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: stableCallback,
        });
      }
    };

    // ✅ FIX: if GSI script is already in the DOM (e.g. loaded by LoginFormEnhanced
    // or a previous mount of this component), window.google is already available —
    // calling initGSI directly instead of waiting for onload which will never fire.
    if (window.google) {
      initGSI();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]'
    );
    if (existingScript) {
      // Script tag exists but hasn't finished loading yet — attach to its load event.
      existingScript.addEventListener('load', initGSI);
      return () => existingScript.removeEventListener('load', initGSI);
    }

    // No script at all — inject it.
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = initGSI;
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (_) {} };
  }, []);

  const calcStrength = (pw: string) => {
    let s = 0;
    if (pw.length >= 8) s++; if (pw.length >= 12) s++;
    if (/[a-z]/.test(pw)) s++; if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++; if (/[^a-zA-Z0-9]/.test(pw)) s++;
    return Math.min(s, 5);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (name === 'password') setStrength(calcStrength(value));
  };

  const strengthColors = ['bg-red-500', 'bg-red-400', 'bg-yellow-400', 'bg-yellow-300', 'bg-green-400', 'bg-green-500'];
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  const strengthTextColors = ['text-red-400', 'text-red-400', 'text-yellow-400', 'text-yellow-300', 'text-green-400', 'text-green-400'];

  /** Drop the spent token and ask the widget for a fresh challenge. */
  const resetCaptcha = () => {
    setCaptchaToken(null);
    setCaptchaResetKey(k => k + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    if (backendStatus === 'offline') { setError('Server is offline. Please try again.'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (strength < 3) { setError('Password too weak — use uppercase, lowercase, numbers and symbols'); return; }
    if (!captchaToken) { setError('Please complete the "I am not a robot" verification below'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username, email: formData.email,
          password: formData.password, captchaToken,
        }),
        signal: AbortSignal.timeout(10000),
      });
      const ct = response.headers.get('content-type');
      if (!ct?.includes('application/json')) throw new Error(`Server error (${response.status})`);
      const data = await response.json();
      if (!response.ok) {
        // The token we just sent is spent regardless of why the call failed —
        // retrying with it would fail Cloudflare's duplicate check. Re-challenge.
        resetCaptcha();
        setError(data.error || `Registration failed (${response.status})`);
        return;
      }
      // ⚠️ NO SESSION HERE. /register stopped issuing one when the email
      // verification gate landed — the account must confirm its address first,
      // and a session at this point would route straight around the gate.
      if (data.requiresVerification) {
        setAwaitingVerification({
          email: data.email || formData.email,
          emailSent: data.verificationEmailSent !== false,
        });
        return;
      }

      // A build of the backend that predates the gate still signs the user in.
      if (!data.accessToken) throw new Error('No access token received');
      localStorage.setItem('authToken', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      onRegisterSuccess(data.accessToken, data.user, data.refreshToken);
    } catch (err: any) {
      resetCaptcha();
      if (err.name === 'AbortError' || err.name === 'TimeoutError') setError('Request timed out. Please try again.');
      else if (err instanceof TypeError && err.message.includes('fetch')) { setError('Network error.'); setBackendStatus('offline'); }
      else setError(err.message || 'Something went wrong.');
    } finally { setLoading(false); }
  };

  // Registration succeeded and the account is waiting on its confirmation
  // link. Replaces the form entirely: re-submitting would only report "Email
  // already registered", which reads as a failure of a signup that worked.
  if (awaitingVerification) {
    return (
      <CheckYourInbox
        email={awaitingVerification.email}
        emailSent={awaitingVerification.emailSent}
        dark={dark}
        onBackToLogin={onSwitchToLogin ?? (() => { window.location.href = '/login'; })}
      />
    );
  }

  const d = dark;
  const card = d ? 'bg-gray-900/95 border-gray-700/60 shadow-2xl shadow-black/50' : 'bg-white border-gray-200 shadow-2xl shadow-gray-300/40';
  const inputCls = `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
    d ? 'bg-gray-800/70 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:bg-gray-800 focus:ring-2 focus:ring-blue-500/20'
      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20'
  } disabled:opacity-40 disabled:cursor-not-allowed`;
  const lbl = `block text-xs font-semibold uppercase tracking-wider mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`;
  const iconCls = `absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${d ? 'text-gray-500' : 'text-gray-400'}`;
  const eyeCls = `absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 cursor-pointer transition-colors ${d ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`;

  return (
    <div className={`rounded-2xl border p-8 w-full max-w-md mx-auto backdrop-blur-xl ${card}`}>
      <div className="text-center mb-7">
        <h2 className={`text-2xl font-black mb-1.5 ${d ? 'text-white' : 'text-gray-900'}`}>Create your account</h2>
        <p className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>Join thousands gaining a data-driven edge</p>
      </div>

      {/* Status banner */}
      {backendStatus === 'checking' && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-5 text-sm ${d ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400' : 'bg-yellow-50 border border-yellow-200 text-yellow-700'}`}>
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" /><span>Connecting to server…</span>
        </div>
      )}
      {backendStatus === 'offline' && (
        <div className={`px-4 py-3 rounded-xl mb-5 ${d ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
          <div className={`flex items-center gap-2 text-sm font-semibold mb-1.5 ${d ? 'text-red-400' : 'text-red-700'}`}>
            <AlertCircle className="w-4 h-4" />Server offline
          </div>
          <p className={`text-xs mb-2 ${d ? 'text-red-400/70' : 'text-red-500'}`}>Cannot reach backend server.</p>
          <button type="button" onClick={checkHealth} className="text-xs px-3 py-1.5 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors">Retry</button>
        </div>
      )}
      {backendStatus === 'online' && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl mb-5 text-xs font-medium ${d ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-green-50 border border-green-200 text-green-700'}`}>
          <CheckCircle className="w-3.5 h-3.5" />Connected to server
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Username */}
        <div>
          <label className={lbl}>Username</label>
          <div className="relative">
            <User className={iconCls} />
            <input type="text" name="username" value={formData.username} onChange={handleChange}
              required minLength={3} autoComplete="username" disabled={backendStatus !== 'online'}
              placeholder="yourusername" className={`${inputCls} pl-10`} />
          </div>
        </div>
        {/* Email */}
        <div>
          <label className={lbl}>Email address</label>
          <div className="relative">
            <Mail className={iconCls} />
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              required autoComplete="email" disabled={backendStatus !== 'online'}
              placeholder="you@example.com" className={`${inputCls} pl-10`} />
          </div>
        </div>
        {/* Password */}
        <div>
          <label className={lbl}>Password</label>
          <div className="relative">
            <Lock className={iconCls} />
            <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password}
              onChange={handleChange} required minLength={8} autoComplete="new-password"
              disabled={backendStatus !== 'online'} placeholder="Min. 8 characters"
              className={`${inputCls} pl-10 pr-11`} />
            <button type="button" onClick={() => setShowPassword(p => !p)} className={eyeCls}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formData.password && (
            <div className="mt-2 space-y-1">
              <div className="flex gap-1">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= strength ? strengthColors[strength] : d ? 'bg-gray-700' : 'bg-gray-200'}`} />
                ))}
              </div>
              <p className={`text-xs ${strengthTextColors[strength]}`}>{strengthLabels[strength]}</p>
            </div>
          )}
        </div>
        {/* Confirm */}
        <div>
          <label className={lbl}>Confirm password</label>
          <div className="relative">
            <Lock className={iconCls} />
            <input type={showConfirm ? 'text' : 'password'} name="confirmPassword"
              value={formData.confirmPassword} onChange={handleChange} required
              autoComplete="new-password" disabled={backendStatus !== 'online'} placeholder="Repeat password"
              className={`${inputCls} pl-10 pr-11 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-500 focus:border-red-500' : ''}`} />
            <button type="button" onClick={() => setShowConfirm(p => !p)} className={eyeCls}>
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
          )}
        </div>

        {/* Human check — ALWAYS rendered; the backend requires the token.
            The Cloudflare widget is configured INVISIBLE, so it paints no box
            and there is nothing for the user to click. This status line is the
            only signal that the check ran, which is why it reports live state
            instead of being a static label: "Verified" appears exactly when a
            token lands, i.e. exactly when Create Account becomes clickable.
            A failed check surfaces its own red message from <Turnstile>. */}
        <div>
          <div
            /* Deliberately NOT `${lbl} flex …`: lbl carries `block` and a
               text-gray-*, and Tailwind resolves conflicting utilities by
               stylesheet order, not class-attribute order — so the overrides
               would not reliably win. Spelled out instead. */
            className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2 ${
              captchaToken ? 'text-emerald-500' : d ? 'text-gray-400' : 'text-gray-500'
            }`}
            aria-live="polite"
          >
            {captchaToken ? '✓ Verified — you’re human' : 'Verifying you’re human…'}
          </div>
          <Turnstile siteKey={TURNSTILE_SITE_KEY}
            onSuccess={tok => { setCaptchaToken(tok); setError(null); }}
            onError={() => setCaptchaToken(null)}
            onExpire={() => setCaptchaToken(null)}
            resetKey={captchaResetKey}
            theme={d ? 'dark' : 'light'} size="normal" />
        </div>

        {/* Error */}
        {error && (
          <div className={`flex gap-2.5 px-4 py-3 rounded-xl text-sm ${d ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}

        {/* Submit */}
        <button type="submit"
          disabled={loading || !captchaToken || backendStatus !== 'online'}
          className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/25 disabled:shadow-none mt-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Creating account…</> : <><span>Create Account</span><ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>

      {/* Google — always visible; errors surfaced via handleGoogleClick */}
      <>
        <div className="flex items-center gap-3 my-5">
          <div className={`flex-1 h-px ${d ? 'bg-gray-700' : 'bg-gray-200'}`} />
          <span className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>or continue with</span>
          <div className={`flex-1 h-px ${d ? 'bg-gray-700' : 'bg-gray-200'}`} />
        </div>
        <button
          type="button"
          disabled={loading || backendStatus === 'checking'}
          onClick={handleGoogleClick}
          className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border text-sm font-medium transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed ${
            d
              ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700 hover:border-gray-600'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Sign up with Google
        </button>
      </>

      {/* Terms */}
      <p className={`text-xs text-center mt-5 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
        By signing up you agree to our{' '}
        <a href="/terms" className={`underline ${d ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}>Terms</a>{' '}and{' '}
        <a href="/privacy" className={`underline ${d ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}>Privacy Policy</a>.
      </p>

      {/* Switch */}
      {onSwitchToLogin && (
        <div className={`mt-6 pt-6 border-t text-center ${d ? 'border-gray-800' : 'border-gray-100'}`}>
          <span className={`text-sm ${d ? 'text-gray-400' : 'text-gray-600'}`}>Already have an account? </span>
          <button type="button" onClick={onSwitchToLogin} disabled={backendStatus !== 'online'}
            className={`text-sm font-semibold ${d ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} disabled:opacity-50 transition-colors`}>
            Sign in instead
          </button>
        </div>
      )}

      <div className={`flex items-center justify-center gap-1.5 mt-5 text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>
        <Shield className="w-3 h-3" />Protected by Cloudflare Turnstile
      </div>
    </div>
  );
};

export default RegistrationForm;
